import {
  addDays,
  differenceInMinutes,
  format,
  isBefore,
  isToday,
  isYesterday,
  startOfDay,
} from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  IFutureServiceHours,
  IRestaurantTimeSlot,
  useGetRestaurantQuery,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { OrderStatus } from '@fhs-legacy/frontend/src/state/order';
import { ServiceMode, useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import {
  filterPastPickupSlots,
  getDateRoundUpToNearestFiveMinutes,
  getDefaultPickupTime,
  getEarliestAvailablePickupTime,
  getFirstAvailableSlotTime,
  getOrderPickupScheduleKey,
} from './earliest-pickup-time';
import { IDatePickerData, ISelectedDate, ITimePickerData } from './types';
import { usePickupSlotRefreshTick } from './use-pickup-slot-refresh-tick';
import { useRestaurantOpenStatus } from './use-restaurant-open-status';

export interface UseDatePickerOptionsResult {
  datePickerData: IDatePickerData[] | undefined;
  firstAvailableDate: Date | undefined;
  firstOptionFormatted: ISelectedDate | undefined;
  earliestPickupTimeInSeconds: number;
  /** Today's store open time for the active service mode, when known. */
  todayStoreOpensAt: Date | undefined;
  /** max(now + lead time, store open + lead time), on a 5-minute boundary */
  earliestAvailablePickupTime: Date;
  /** First filtered slot, or earliest available instant — not derived from fireOrderIn */
  defaultPickupTime: Date | undefined;
  /** Increments every 5 minutes to re-filter past slots */
  slotRefreshTick: number;
  /** Stable across 5-minute client filter; changes on repricing / new server slots */
  pickupScheduleKey: string;
  isLoadingData: boolean;
  hasError: boolean;
  isRestaurantOpen: boolean;
  warningMessage: string;
}

const MINUTE_IN_MS = 60000;

const FUTURE_ORDERING_OPTIONS = {
  timeInterval: 15,
  days: 7,
  minutesBeforeStoreCloses: 15,
};

const TIME_FIRED_OPTIONS = {
  timeInterval: 15,
  days: 1,
  length: 5,
  minutesBeforeStoreCloses: 15,
};

export function useDatePickerOptions(): UseDatePickerOptionsResult {
  const enableFutureOrdering = useFlag('enable-future-ordering');
  const enableTodayAvailableOptionsFromBackend = useFlag(
    'enable-today-available-pickup-options-from-backend'
  );

  const { serverOrder, orderStatus } = useCartContext();
  const { store } = useStoreContext();
  const { serviceMode } = useServiceModeContext();
  const { isRestaurantOpen } = useRestaurantOpenStatus();

  const earliestPickupTimeInSeconds =
    (serverOrder as { earliestPickupTimeInSeconds?: number })?.earliestPickupTimeInSeconds ?? 0;

  const { data, loading, error } = useGetRestaurantQuery({
    variables: { storeNumber: store.number },
    skip: !store,
    fetchPolicy: 'cache-first',
  });

  const todayStoreOpensAt = useMemo(
    () => getTodayStoreOpensAt(data?.restaurant?.futureServiceHours, serviceMode),
    [data?.restaurant?.futureServiceHours, serviceMode]
  );

  const [baseDatePickerData, setBaseDatePickerData] = useState<IDatePickerData[]>();
  const [firstOptionFormatted, setFirstOptionFormatted] = useState<ISelectedDate>();
  const slotRefreshTick = usePickupSlotRefreshTick();

  const datePickerData = useMemo(() => {
    void slotRefreshTick;
    return filterPastPickupSlots(baseDatePickerData, earliestPickupTimeInSeconds, {
      storeOpensAt: todayStoreOpensAt,
    });
  }, [baseDatePickerData, earliestPickupTimeInSeconds, slotRefreshTick, todayStoreOpensAt]);

  const firstAvailableDate = useMemo(
    () =>
      firstOptionFormatted
        ? new Date(
            firstOptionFormatted.year,
            firstOptionFormatted.month,
            firstOptionFormatted.day,
            firstOptionFormatted.hour,
            firstOptionFormatted.minutes
          )
        : undefined,
    [firstOptionFormatted]
  );

  const getDatePickerData = useCallback(() => {
    if (!data?.restaurant?.futureServiceHours?.length) {
      return undefined;
    }

    const storeData = data.restaurant.futureServiceHours.find(
      slot => slot.serviceMode === serviceMode
    );

    if (!storeData?.timeSlots?.length) {
      return undefined;
    }

    return buildDatePickerData(
      storeData.timeSlots as IRestaurantTimeSlot[],
      enableFutureOrdering,
      enableTodayAvailableOptionsFromBackend,
      isRestaurantOpen,
      earliestPickupTimeInSeconds,
      (serverOrder as { availableTimeOptions?: string[] })?.availableTimeOptions || []
    );
  }, [
    data,
    enableTodayAvailableOptionsFromBackend,
    serviceMode,
    enableFutureOrdering,
    isRestaurantOpen,
    earliestPickupTimeInSeconds,
    serverOrder,
  ]);

  useEffect(() => {
    if (data?.restaurant && orderStatus === OrderStatus.PRICE_SUCCESSFUL) {
      setBaseDatePickerData(getDatePickerData());
    }
  }, [getDatePickerData, data, orderStatus]);

  // Keep firstOptionFormatted aligned when the 5-minute filter advances the first slot.
  useEffect(() => {
    const firstTime = getFirstAvailableSlotTime(datePickerData);
    if (!firstTime) {
      return;
    }

    setFirstOptionFormatted({
      year: firstTime.getFullYear(),
      month: firstTime.getMonth(),
      day: firstTime.getDate(),
      hour: firstTime.getHours(),
      minutes: firstTime.getMinutes(),
    });
  }, [datePickerData]);

  const isLoadingData =
    loading ||
    !orderStatus ||
    ![
      OrderStatus.PRICE_SUCCESSFUL,
      OrderStatus.INSERT_SUCCESSFUL,
      OrderStatus.PRICE_ERROR,
    ].includes(orderStatus);

  const hasError = !!error || orderStatus === OrderStatus.PRICE_ERROR;

  const warningMessage =
    enableTodayAvailableOptionsFromBackend &&
    (serverOrder as { warningMessage?: string })?.warningMessage
      ? (serverOrder as { warningMessage?: string }).warningMessage!
      : '';

  const earliestAvailablePickupTime = useMemo(() => {
    void slotRefreshTick;
    return getEarliestAvailablePickupTime(earliestPickupTimeInSeconds, {
      storeOpensAt: todayStoreOpensAt,
    });
  }, [earliestPickupTimeInSeconds, slotRefreshTick, todayStoreOpensAt]);

  const defaultPickupTime = useMemo(() => {
    void slotRefreshTick;
    return getDefaultPickupTime({
      earliestPickupTimeInSeconds,
      datePickerData,
      storeOpensAt: todayStoreOpensAt,
    });
  }, [earliestPickupTimeInSeconds, datePickerData, slotRefreshTick, todayStoreOpensAt]);

  const pickupScheduleKey = useMemo(() => {
    const availableTimeOptions =
      (serverOrder as { availableTimeOptions?: string[] })?.availableTimeOptions ?? [];
    const rbiOrderId = (serverOrder as { rbiOrderId?: string })?.rbiOrderId ?? '';

    return getOrderPickupScheduleKey(earliestPickupTimeInSeconds, availableTimeOptions, rbiOrderId);
  }, [earliestPickupTimeInSeconds, serverOrder]);

  return {
    datePickerData,
    firstAvailableDate,
    firstOptionFormatted,
    earliestPickupTimeInSeconds,
    todayStoreOpensAt,
    earliestAvailablePickupTime,
    defaultPickupTime,
    slotRefreshTick,
    pickupScheduleKey,
    isLoadingData,
    hasError,
    isRestaurantOpen,
    warningMessage,
  };
}

/**
 * Build complete date picker data from raw time slots.
 * Handles sanitization, time slot generation, and validation.
 */
function buildDatePickerData(
  rawTimeSlots: IRestaurantTimeSlot[],
  enableFutureOrdering: unknown,
  enableTodayAvailableOptionsFromBackend: unknown,
  isRestaurantOpen: boolean,
  earliestPickupTimeInSeconds: number,
  todayAvailableTimeOptions: string[]
): IDatePickerData[] {
  const options = enableFutureOrdering ? FUTURE_ORDERING_OPTIONS : TIME_FIRED_OPTIONS;
  const earliestPickupMinutes = earliestPickupTimeInSeconds / 60;

  // Helper function to find or create a day slot
  const findDayInTimeSlots = (slots: IRestaurantTimeSlot[], day: string): IRestaurantTimeSlot => {
    const dayToFind = slots.find(slot => slot?.date === day);
    if (dayToFind) {
      return dayToFind;
    }
    // Generate missing date and presume the store is closed that day
    const openAndCloseHours = new Date(day + 'T00:00:00');
    openAndCloseHours.setHours(0);
    openAndCloseHours.setMinutes(0);
    openAndCloseHours.setSeconds(0);
    const formattedOpenAndCloseHours = format(openAndCloseHours, 'yyyy-MM-dd HH:mm:ss');
    const formattedDate = format(openAndCloseHours, 'yyyy-MM-dd');
    return {
      date: formattedDate,
      isClosed: true,
      opens: formattedOpenAndCloseHours,
      closes: formattedOpenAndCloseHours,
    };
  };

  // Generate time slots for consecutive days starting from today
  // For catering, skip the first day (cateringDaysAhead)
  const sanitizedTimeSlots: IRestaurantTimeSlot[] = [];

  for (let i = 0; i < options.days; i++) {
    const day = format(addDays(startOfDay(new Date()), i), 'yyyy-MM-dd');
    const timeSlot = findDayInTimeSlots(rawTimeSlots, day);
    sanitizedTimeSlots.push(timeSlot);
  }

  // Add yesterday as an option to show past midnight available options for an open store.
  // e.g. store closes 2 am. and it is currently 1 am.
  const yesterdaySlot = rawTimeSlots.find(slot => isYesterday(new Date(slot.date)));
  if (
    yesterdaySlot &&
    sanitizedTimeSlots.length > 0 &&
    isToday(new Date(sanitizedTimeSlots[0].date)) &&
    isBefore(new Date(yesterdaySlot.closes), new Date(yesterdaySlot.opens))
  ) {
    sanitizedTimeSlots.unshift({
      date: yesterdaySlot.date,
      closes: format(addDays(new Date(yesterdaySlot.closes), 1), 'yyyy-MM-dd HH:mm:ss'),
      opens: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      isClosed: false,
    });
  }

  // Build date picker data for each day
  const result = sanitizedTimeSlots.map((timeSlot, index) => {
    const { opens = '', closes = '', date = '', isClosed } = timeSlot;
    const isFirstDay = index === 0;
    const dateTimeWithTime = date + 'T12:00:00';

    const timeSlotOpens = parseLocalDate(opens);
    let timeSlotCloses = parseLocalDate(closes);

    // Update timeSlotCloses if the store closes past midnight (e.g. 2 am)
    const isYesterdaySlot = isYesterday(new Date(dateTimeWithTime));
    if (!isYesterdaySlot) {
      const storeClosesPastMidnight = isBefore(timeSlotCloses, timeSlotOpens);
      timeSlotCloses = storeClosesPastMidnight ? addDays(timeSlotCloses, 1) : timeSlotCloses;
    }

    // Generate time slots from now for today (if store is open) and from the open date for the rest of the week
    const startingDate = getStartingDate(isRestaurantOpen, timeSlotOpens, isYesterdaySlot);

    // We need to disable the first day in the array in case the store is already closed for the day
    const isFirstDayNotAvailable = getIsFirstDayNotAvailable(timeSlotCloses);

    const lastPossibleSlot =
      timeSlotCloses.getTime() - options.minutesBeforeStoreCloses * MINUTE_IN_MS;

    const offset = enableFutureOrdering ? earliestPickupMinutes : 0;
    const length = enableFutureOrdering
      ? differenceInMinutes(timeSlotCloses, startingDate) / options.timeInterval
      : TIME_FIRED_OPTIONS.length;

    let dateOptions: ITimePickerData[] = [];
    const earliestAvailablePickupTime = getEarliestAvailablePickupTime(
      earliestPickupTimeInSeconds,
      isFirstDay && !isYesterdaySlot ? { storeOpensAt: timeSlotOpens } : {}
    );

    // Get available time options from BE for today if flag is enabled
    if (
      enableTodayAvailableOptionsFromBackend &&
      isFirstDay &&
      todayAvailableTimeOptions.length > 0
    ) {
      dateOptions = todayAvailableTimeOptions
        .map(timeOption => ({
          time: getDateRoundUpToNearestFiveMinutes(parseLocalDate(timeOption)),
        }))
        .filter(
          option =>
            option.time >= earliestAvailablePickupTime && option.time.getTime() < lastPossibleSlot
        );
    } else {
      // Generate time slots based on interval
      const totalSlots = Math.floor(length);
      for (let i = 0; i < totalSlots; i++) {
        const slotTime = getDateRoundUpToNearestFiveMinutes(
          new Date(startingDate.getTime() + (i * options.timeInterval + offset) * MINUTE_IN_MS)
        );
        if (
          slotTime.getTime() >= earliestAvailablePickupTime.getTime() &&
          slotTime.getTime() < lastPossibleSlot
        ) {
          dateOptions.push({ time: slotTime });
        }
      }
    }

    // Check if this day should be disabled
    const isUnavailable = !dateOptions.length || isClosed || isFirstDayNotAvailable;

    return { date, isUnavailable, options: dateOptions };
  });

  return result;
}

function getTodayStoreOpensAt(
  futureServiceHours: ReadonlyArray<IFutureServiceHours> | null | undefined,
  serviceMode: ServiceMode | null | undefined
): Date | undefined {
  if (!futureServiceHours?.length || !serviceMode) {
    return undefined;
  }

  const storeData = futureServiceHours.find(slot => slot.serviceMode === serviceMode);
  const today = format(startOfDay(new Date()), 'yyyy-MM-dd');
  const todaySlot = storeData?.timeSlots?.find(
    (slot): slot is IRestaurantTimeSlot => slot != null && slot.date === today
  );

  if (!todaySlot?.opens || todaySlot.isClosed) {
    return undefined;
  }

  return parseLocalDate(todaySlot.opens);
}

function parseLocalDate(dateString: string): Date {
  // If it already has a 'T' separator, use it as-is
  if (dateString.includes('T')) {
    return new Date(dateString);
  }
  // Replace space with 'T' to ensure it's parsed as local time
  return new Date(dateString.replace(' ', 'T'));
}

function getStartingDate(
  isRestaurantOpen: boolean,
  timeSlotOpens: Date,
  isYesterdaySlot: boolean
): Date {
  // Always start from now if the current slot is yesterday since this is for past midnight options
  if (isYesterdaySlot) {
    return new Date();
  }
  return isToday(timeSlotOpens) && isRestaurantOpen && isBefore(timeSlotOpens, new Date())
    ? new Date() // Start options from now - store is already open
    : timeSlotOpens;
}

function getIsFirstDayNotAvailable(timeSlotCloses: Date): boolean {
  return isToday(timeSlotCloses) && new Date().getTime() > timeSlotCloses.getTime();
}
